"use client"
import { useEffect, useRef, useState } from "react"
import { twMerge } from "tailwind-merge"
import clsx from "clsx/lite"
import Image from "next/image"
import RightArrow from "@icons/RightArrow.svg?react"
import Paginator from "@components/Paginator"
import Link from "next/link"
import { upperFirst, isEmpty, chunk as _chunk, before } from "lodash"
import FilterBar from "@components/FilterBar"
import useBreakpoints from "@hooks/useBreakpoints"
import { snake } from "@utils/String"

export default ({
    header = "Project Gallery",
    subtitle = "We invite you to take a look at the custom homes we have built.",
    filters = ["all"],
    items,
}) => {
    const firstPerPage = useBreakpoints({ md: 4, lg: 5 }, 1)
    const otherPerPage = useBreakpoints({ md: 4, lg: 9 }, 1)
    const content = useRef()

    const chunk = (array) => {
        // Splat the array so we don't modify the original.
        return [
            [...array].splice(0, firstPerPage),
            ..._chunk(array.slice(firstPerPage, array.length), otherPerPage),
        ]
    }

    const [activeFilters, setActiveFilters] = useState([filters[0]])
    const [data, setData] = useState(chunk(items))
    const [page, setPage] = useState(0)

    useEffect(() => {
        if (activeFilters.includes("all")) {
            setData(chunk(items))
            return
        }

        const filtered = items.filter((item) =>
            item.categories.some((category) =>
                activeFilters.includes(category),
            ),
        )

        setData(chunk(filtered))
    }, [activeFilters, items, firstPerPage, otherPerPage])

    const handlePaginate = (page) => {
        setPage(page)

        setTimeout(
            () =>
                window.scrollTo({
                    top: content.current.offsetTop - 200,
                    behavior: "smooth",
                }),
            150,
        )
    }

    const addFilter = (filter) => {
        setPage(0)
        setActiveFilters((prev) => {
            let next = prev.includes("all") || filter === "all" ? [] : [...prev]

            if (!next.includes(filter)) {
                next.push(filter)
                return next
            }

            if (next.includes(filter)) {
                let filters = next.filter((item) => item != filter)

                return isEmpty(filters) ? ["all"] : filters
            }
        })
    }

    const getRowClass = (count) => {
        if (count > 6) return "grid-rows-1 md:grid-rows-2 lg:grid-rows-3"
        if (count > 3) return "grid-rows-1 md:grid-rows-2"
        return "grid-rows-1 md:grid-rows-1"
    }

    return (
        <section id="gallery" className="section">
            <div className="container max-w-5xl flex flex-col mx-auto px-10">
                <h1 className="heading center">{header}</h1>
                <p className="subheading center">{subtitle}</p>
                {filters.length < 2 && (
                    <div className="mb-12 pr-10">
                        <FilterBar
                            filters={filters}
                            active={activeFilters}
                            callback={(filter) => addFilter(filter)}
                        />
                    </div>
                )}
                <div
                    ref={content}
                    className={clsx(
                        "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 w-full gap-5 mb-4",
                        getRowClass(data[page].length),
                    )}
                >
                    {isEmpty(data) && (
                        <div className="col-span-3 flex">
                            <p className="text-caviar mx-auto text-2xl">
                                No plans found for your filters.
                            </p>
                        </div>
                    )}
                    {!isEmpty(data) &&
                        data[page].map((plan, idx) => (
                            <Plan
                                key={`builder-gallery-plans-${idx}`}
                                colClass={
                                    page === 0 && idx === 3 ?
                                        "lg:col-span-2"
                                    :   "col-span-1"
                                }
                                title={plan.title}
                                id={plan.id}
                                cover={plan.cover}
                                info={plan.info}
                                variant={plan.variant}
                                href={`/builds/${snake(plan.title)}`}
                                page={page}
                            />
                        ))}
                </div>
                {!isEmpty(data) && (
                    <Paginator
                        total={data.length}
                        current={page}
                        callback={(idx) => handlePaginate(idx)}
                    />
                )}
            </div>
        </section>
    )
}

const Plan = ({
    href = "#",
    colClass = "col-span-1",
    title,
    id,
    variant = "",
    info,
    cover,
    page,
    className = "",
}) => {
    return (
        <Link
            href={href}
            className={twMerge(
                clsx(
                    "bg-sticks-and-stones border-b-6 border-garden-grove overflow-hidden relative group row-span-1 col-span-1",
                    `${colClass}`,
                    className,
                ),
            )}
        >
            <div className="w-full h-full pb-[25%] md:pb-0">
                <Image
                    src={process.env.NEXT_PUBLIC_STATIC_PREFIX + cover.url}
                    width={cover.width}
                    height={cover.height}
                    alt={cover.alt}
                    priority
                    className="object-cover w-full h-full aspect-square hover:scale-105 transition-transform duration-300 hover:blur-[1px]"
                />
            </div>
            {colClass !== "col-span-1" ?
                <div className="opacity-100 group-hover:opacity-100 bg-arugula absolute group-focus:opacity-100 lg:opacity-0 z-20 w-1/2 top-[50%] left-[50%] py-6 px-6 -translate-[50%] transition-opacity duration-200 group/item">
                    <div className="flex justify-between w-full mb-8">
                        <div className="flex flex-col">
                            <h1 className="text-white-snow font-bold text-2xl">
                                {title}
                            </h1>
                            <span>Plan {id}</span>
                        </div>
                        <div className="border-4 border-garden-grove p-1 ml-4 opacity-100 group-hover/item:opacity-100 transition-opacity">
                            <RightArrow className="size-10 fill-downy -rotate-45 transition-transform group-hover/item:rotate-0" />
                        </div>
                    </div>
                    <div className="inline-flex text-twilight-gray">
                        {Object.keys(info).map((key) => (
                            <span key={key} className="mr-2">
                                <span className="text-white-snow font-bold mr-1">
                                    {info[key]}
                                </span>
                                {upperFirst(key)}
                            </span>
                        ))}
                    </div>
                </div>
            :   <div className="bg-arugula z-20 w-full absolute bottom-0 opacity-100 lg:opacity-0 group-hover:opacity-100 group-focus:opacity-100 flex py-3 md:py-6 px-6 justify-between group/item transition-opacity duration-200">
                    <div className="flex flex-col">
                        <h1 className="text-white-snow font-bold text-2xl">
                            {title}
                        </h1>
                        <span>
                            Plan {id}
                            {variant ?? ""}
                        </span>
                    </div>
                    <div className="border-4 border-garden-grove p-1 ml-4 opacity-100 group-hover/item:opacity-100 transition-opacity">
                        <RightArrow className="size-10 fill-downy -rotate-45 transition-transform group-hover/item:rotate-0" />
                    </div>
                </div>
            }
        </Link>
    )
}
